import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { savePostAction, unsavePostAction, getSavedPostsAction, increaseSavedByCount, decreaseSavedByCount, addCommentAction, getPostAction, updatePostAction } from "../../redux/actions/postActions";
import { followUserAndFetchData, unfollowUserAction, getFollowingUsersAction, createFollowRequestAction, getSentFollowRequestsAction } from "../../redux/actions/userActions";
import * as types from "../../redux/constants/userConstants";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { getCommunityAction } from "../../redux/actions/communityActions";
import { processRichText } from "../../utils/richTextProcessor";
import debounce from "lodash.debounce";
import { searchUsers } from "../../redux/api/userAPI";
import Like from "./Like";
import CommentForm from "../form/CommentForm";
import { HiOutlineChatBubbleOvalLeft, HiOutlineEllipsisVertical } from "react-icons/hi2";
import { FaRegBookmark, FaBookmark, FaTrash, FaShare, FaLock, FaUserPlus, FaUserMinus } from "react-icons/fa";
import DeleteModal from "../modals/DeleteModal";
import EditPostModal from "../modals/EditPostModal";
import PublishPostModal from "../modals/PublishPostModal";
import { IoIosArrowBack } from "react-icons/io";
import CommonLoading from "../loader/CommonLoading";
import "react-photo-view/dist/react-photo-view.css";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { RichTextContent } from "../../utils/richTextProcessor";
import { Transition } from "@headlessui/react";
import useMediaQuery from "../../hooks/useMediaQuery";

// Local helper: combine rich text processing and @mention linking for comments
const renderRichWithMentions = (text) => {
  if (!text) return "";
  // First, process general rich text
  let html = processRichText(text);
  // Then linkify mentions
  html = html.replace(/@(\w+)/g, '<a href="/user/username/$1" class="text-primary-700 font-semibold hover:underline">@$1</a>');
  return html;
};

// --- Suggestion Dropdown ---
const MentionSuggestions = ({ suggestions, onSelect }) => {
  if (!suggestions.length) return null;

  return (
    <div className="absolute bg-white border border-gray-200 rounded-md shadow-md mt-1 w-64 z-50">
      {suggestions.map((user) => (
        <div
          key={user._id}
          className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 cursor-pointer"
          onClick={() => onSelect(user)}
        >
          <img
            src={user.avatar}
            alt={user.name}
            className="w-6 h-6 rounded-full object-cover"
          />
          <span className="text-sm">{user.name}</span>
        </div>
      ))}
    </div>
  );
};

const PostView = ({ post, userData }) => {
  const isSmallScreen = useMediaQuery("(max-width: 912px)");

  const [loading, setLoading] = useState(true);
  const [showCommunityDropdown, setShowCommunityDropdown] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [unfollowLoading, setUnfollowLoading] = useState(false);
  const [activeReply, setActiveReply] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [visibleComments, setVisibleComments] = useState(25);
  const [showReplies, setShowReplies] = useState({});
  const communityDropdownRef = useRef(null);
  const dropdownRef = useRef(null);
  const textareaRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedPosts = useSelector((state) => state.posts?.savedPosts || []);
  const followingUsers = useSelector((state) => state.user?.followingUsers || []);

  // --- Debounced search for mentions ---
  const fetchSuggestions = debounce(async (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }
    const { data } = await searchUsers(query);
    setSuggestions(data?.users || []);
    setShowSuggestions(true);
  }, 300);

  // --- Detect "@" and search ---
  const handleReplyChange = (e) => {
    const value = e.target.value;
    setReplyContent(value);

    const match = value.match(/@(\w*)$/);
    if (match) {
      fetchSuggestions(match[1]);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // --- Insert username ---
  const handleSelectUser = (user) => {
    const value = replyContent.replace(/@\w*$/, `@${user.name} `);
    setReplyContent(value);
    setSuggestions([]);
    setShowSuggestions(false);

    // focus back textarea
    textareaRef.current.focus();
  };

  // --- Handle reply submit ---
  const handleReplySubmit = async (parentCommentId) => {
    if (!replyContent.trim()) return;

    setReplyLoading(true);
    try {
      await dispatch(addCommentAction(post?._id, {
        content: replyContent,
        postId: post?._id,
        parentCommentId: parentCommentId
      }));
      setReplyContent("");
      setActiveReply(null);
      // Refresh the post data to get updated comments
      await dispatch(getPostAction(post?._id));
    } catch (error) {
      console.error("Error adding reply:", error);
    } finally {
      setReplyLoading(false);
    }
  };

  // --- Handle load more comments ---
  const handleLoadMore = () => {
    setVisibleComments(prev => prev + 10);
  };

  // --- Handle toggle replies ---
  const toggleReplies = (commentId) => {
    setShowReplies(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  // Handle invalid post data safely
  const postData = post && typeof post === 'object' ? post : {};
  const {
    content,
    fileUrl,
    fileType,
    youtubeVideo,
    user = {},
    community = null,
    communities = [],
    audience = "Private",
    createdAt,
    comments = [],
    allowComments = true
  } = postData;

  // Helper function to get community display info
  const getCommunityDisplay = () => {
    try {
      // Check for communities array
      if (communities &&
        Array.isArray(communities) &&
        communities.length > 0 &&
        communities[0] &&
        typeof communities[0] === 'object' &&
        communities[0]._id &&
        communities[0].name &&
        communities[0]._id !== null &&
        communities[0].name !== null) {

        if (communities.length === 1) {
          return {
            type: 'single',
            name: communities[0].name,
            id: communities[0]._id,
            allCommunities: communities
          };
        } else {
          return {
            type: 'multiple',
            name: communities[0].name,
            id: communities[0]._id,
            allCommunities: communities,
            remainingCount: communities.length - 1
          };
        }
      }

      // Check for audience types
      if (audience === "Private") {
        return {
          type: 'audience',
          name: "Private",
          id: null
        };
      }

      if (audience === "Followers") {
        return {
          type: 'audience',
          name: "Followers",
          id: null
        };
      }

      if (audience === "Anyone") {
        return {
          type: 'audience',
          name: "Anyone",
          id: null
        };
      }

      return null;
    } catch (error) {
      console.error('Error in getCommunityDisplay:', error);
      return null;
    }
  };

  const communityDisplay = getCommunityDisplay();

  useEffect(() => {
    if (communityDisplay && communityDisplay.id) {
      dispatch(getCommunityAction(communityDisplay.name)).then(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, communityDisplay, loading]);

  // Load saved posts and check if current post is saved (only when authenticated)
  useEffect(() => {
    if (userData?._id) {
      dispatch(getSavedPostsAction());
    }
  }, [dispatch, userData?._id]);

  useEffect(() => {
    if (post && post._id) {
      const savedPostsIds = savedPosts.map(savedPost => savedPost?._id);
      const isPostSaved = savedPostsIds.includes(post._id);
      setIsSaved(isPostSaved);
    }
  }, [savedPosts, post]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (communityDropdownRef.current && !communityDropdownRef.current.contains(event.target)) {
        setShowCommunityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const toggleModal = (value) => {
    setShowModal(value);
  };
  const toggleEditModal = (value) => {
    setShowEditModal(value);
  };
  const togglePublishModal = (value) => {
    setShowPublishModal(value);
  };
  // const [replyTarget, setReplyTarget] = useState(null);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleSavePost = async () => {
    if (isSaving || !post || !post._id) return;


    setIsSaving(true);
    try {
      if (isSaved) {
        await dispatch(unsavePostAction(post._id));
        dispatch(decreaseSavedByCount(post._id));
        setIsSaved(false);
      } else {
        await dispatch(savePostAction(post._id));
        dispatch(increaseSavedByCount(post._id));
        setIsSaved(true);
      }

      // Refresh saved posts to ensure Redux state is updated (only if authenticated)
      if (userData?._id) {
        await dispatch(getSavedPostsAction());
      }
    } catch (error) {
      console.error("Error saving/unsaving post:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleComments = async (enableComments = false) => {
    if (!post || !post._id) return;

    try {
      const formData = new FormData();
      formData.append("allowComments", enableComments.toString());

      await dispatch(updatePostAction(post._id, formData));
      // Refresh the post to get updated data
      await dispatch(getPostAction(post._id));
    } catch (error) {
      console.error("Error toggling comments:", error);
    }
  };

  const handleMakePrivate = async () => {
    if (!post || !post._id) return;

    try {
      const formData = new FormData();
      formData.append("audience", "Private");
      formData.append("communities", JSON.stringify([]));

      await dispatch(updatePostAction(post._id, formData));
      // Refresh the post to get updated data
      await dispatch(getPostAction(post._id));
    } catch (error) {
      console.error("Error making post private:", error);
    }
  };

  // Load following users list on mount if not already loaded
  const hasFetchedFollowingRef = useRef(false);
  useEffect(() => {
    // If followingUsers is already populated, no need to fetch
    if (followingUsers && followingUsers.length > 0) {
      hasFetchedFollowingRef.current = false;
      return;
    }
    
    // Only fetch if user exists, followingUsers is empty, and we haven't already initiated a fetch
    if (userData?._id && !hasFetchedFollowingRef.current) {
      hasFetchedFollowingRef.current = true;
      dispatch(getFollowingUsersAction(100, 0));
    }
  }, [dispatch, userData?._id, followingUsers]);

  // Check if current user is following the post author
  const isFollowingAuthor = useMemo(() => {
    if (!post?.user?._id || !followingUsers || followingUsers.length === 0) {
      return false;
    }
    return followingUsers.some(followingUser => followingUser._id === post.user._id);
  }, [post?.user?._id, followingUsers]);

  const handleFollow = async () => {
    if (!post?.user?._id || followLoading) return;
    
    setFollowLoading(true);
    try {
      // Create a follow request instead of directly following
      const result = await dispatch(createFollowRequestAction(post.user._id));
      if (result?.success) {
        // Refresh sent follow requests to show the new request
        await dispatch(getSentFollowRequestsAction(null, 25, 0));
      }
      // Always refresh the following users list to update the UI
      await dispatch(getFollowingUsersAction(100, 0));
      setShowDropdown(false);
    } catch (error) {
      console.error("Error creating follow request:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!post?.user?._id || unfollowLoading) return;
    
    setUnfollowLoading(true);
    try {
      const result = await dispatch(unfollowUserAction(post.user._id));
      if (result && result.success) {
        // Remove user from followingUsers list
        dispatch({
          type: types.REMOVE_FOLLOWING_USER,
          payload: post.user._id
        });
      }
      setShowDropdown(false);
    } catch (error) {
      console.error("Error unfollowing user:", error);
    } finally {
      setUnfollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-section flex justify-center items-center">
        <CommonLoading />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border pt-4 px-4 pb-3 md:pt-5 md:px-6 md:pb-4 shadow-sm w-full relative">
      {/* Back, Save and 3-dots icons in top-right corner */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <button
          className="text-primary-700 hover:text-primary-800 transition-colors flex items-center gap-1 text-sm font-roboto-flex px-2 py-1 rounded-md hover:bg-primary-50"
          onClick={() => {
            const targetPath = location.state?.from || "/skills-feed";
            navigate(targetPath, {
              state: { scrollToPost: post?._id }
            });
          }}
        >
          <IoIosArrowBack className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleSavePost}
          disabled={isSaving}
          className="text-gray-400 hover:text-primary-600 transition-colors duration-200 flex items-center justify-center p-2 rounded-full hover:bg-white/80 backdrop-blur-sm"
        >
          {isSaved ? (
            <FaBookmark className={`w-4 h-4 text-primary-600`} />
          ) : (
            <FaRegBookmark className={`w-4 h-4`} />
          )}
        </button>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={toggleDropdown}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 flex items-center justify-center p-2 rounded-full hover:bg-white/80 backdrop-blur-sm"
          >
            <HiOutlineEllipsisVertical className="w-5 h-5" />
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 backdrop-blur-sm overflow-hidden">
              {/* Options for post owner */}
              {userData?._id === post?.user?._id && (
                <>
                  {/* Show Publish Post button only for private posts */}
                  {audience === "Private" && (
                    <button
                      onClick={() => {
                        togglePublishModal(true);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-primary-700 hover:bg-primary-50 font-roboto-flex transition-colors duration-200"
                    >
                      <FaShare className="w-4 h-4 text-primary-700" />
                      <span className="font-medium">Publish Post</span>
                    </button>
                  )}
                  
                  {allowComments && (
                    <button
                      onClick={() => {
                        handleToggleComments(false);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-roboto-flex transition-colors duration-200"
                    >
                      <HiOutlineChatBubbleOvalLeft className="w-4 h-4 text-gray-700" />
                      <span className="font-medium">Turn off comments</span>
                    </button>
                  )}
                  
                  {!allowComments && (
                    <button
                      onClick={() => {
                        handleToggleComments(true);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-primary-700 hover:bg-primary-50 font-roboto-flex transition-colors duration-200"
                    >
                      <HiOutlineChatBubbleOvalLeft className="w-4 h-4 text-primary-700" />
                      <span className="font-medium">Turn on comments</span>
                    </button>
                  )}
                  
                  {audience !== "Private" && (
                    <button
                      onClick={() => {
                        handleMakePrivate();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-roboto-flex transition-colors duration-200"
                    >
                      <FaLock className="w-4 h-4 text-gray-700" />
                      <span className="font-medium">Make Private Post</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      toggleModal(true);
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-action hover:bg-action/10 font-roboto-flex transition-colors duration-200"
                  >
                    <FaTrash className="w-4 h-4 text-action" />
                    <span className="font-medium">Delete Post</span>
                  </button>
                </>
              )}

              {/* Options for other users' posts */}
              {userData?._id !== post?.user?._id && (
                <>
                  {isFollowingAuthor ? (
                    <button
                      onClick={handleUnfollow}
                      disabled={unfollowLoading}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-action hover:bg-action/10 font-roboto-flex transition-colors duration-200 disabled:opacity-50"
                    >
                      {unfollowLoading ? (
                        <span className="font-medium">Unfollowing...</span>
                      ) : (
                        <>
                          <FaUserMinus className="w-4 h-4 text-action" />
                          <span className="font-medium">Unfollow</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-primary-700 hover:bg-primary-50 font-roboto-flex transition-colors duration-200 disabled:opacity-50"
                    >
                      {followLoading ? (
                        <span className="font-medium">Following...</span>
                      ) : (
                        <>
                          <FaUserPlus className="w-4 h-4 text-primary-700" />
                          <span className="font-medium">Follow</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {isSmallScreen ?
        <>
          <div className="flex items-start gap-3 mb-1 md:mb-2">
            <div className="flex-shrink-0">
              <img
                className="rounded-full overflow-hidden w-12 h-12 object-cover"
                src={user.avatar}
                alt="user avatar"
                loading="lazy"
              />
            </div>
            <div className="flex-1">
              <div className="flex flex-col">
                {userData?._id === user?._id ? (
                  <Link to="/profile" className="font-semibold text-sm md:text-base capitalize font-roboto-flex">
                    {user?.name}
                  </Link>
                ) : (
                  <Link to={`/profile/${user?.profileUrl || user?._id}`} className="font-semibold text-sm md:text-base capitalize font-roboto-flex">
                    {user?.name}
                  </Link>
                )}
                <div className="flex items-center gap-2">
                  {communityDisplay && (
                    communityDisplay.type === 'audience' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm">
                        {communityDisplay.name}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/skills-feed/${communityDisplay.name}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm"
                        >
                          {communityDisplay.name}
                        </Link>
                        {communityDisplay.type === 'multiple' && communityDisplay.remainingCount > 0 && (
                          <div className="relative" ref={communityDropdownRef}>
                            <button
                              onClick={() => setShowCommunityDropdown(!showCommunityDropdown)}
                              className="text-xs text-primary-700 font-roboto-flex hover:text-primary-800 cursor-pointer"
                            >
                              +{communityDisplay.remainingCount} more
                            </button>

                            <Transition
                              show={showCommunityDropdown}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                                <div className="py-1">
                                  {communityDisplay.allCommunities.slice(1).map((comm, index) => (
                                    <Link
                                      key={comm?._id}
                                      to={`/skills-feed/${comm.name}`}
                                      className="block px-3 py-2 text-xs font-roboto-flex"
                                      onClick={() => setShowCommunityDropdown(false)}
                                    >
                                      <span className="font-roboto-flex inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 shadow-sm">
                                        {comm.name}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </Transition>
                          </div>
                        )}
                      </div>
                    )
                  )}
                  <span className="text-gray-400 font-roboto-flex">|</span>
                  <p className="text-xs text-gray-500 font-roboto-flex">{createdAt}</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            {/* Post Content */}
            <div className="mt-4 mb-4">
              <div className="font-roboto-flex break-words overflow-wrap-anywhere max-w-full text-xs md:text-sm">
                <RichTextContent content={content} />
              </div>

              {/* Media Section */}
              <div className="mt-1">
                {fileUrl && fileType.startsWith("image") ? (
                  <PhotoProvider
                    overlayRender={() => (
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-10 text-white px-3 py-2">
                        <p className="text-xs font-roboto-flex">{user.name}</p>
                        {communityDisplay && (
                          <p className="text-xs font-roboto-flex">{communityDisplay.name}</p>
                        )}
                        <p className="text-xs font-roboto-flex">{createdAt}</p>
                      </div>
                    )}
                  >
                    <PhotoView src={fileUrl}>
                      <div className="w-full max-w-3xl mx-auto">
                        <img
                          src={fileUrl}
                          alt={`${fileType} file`}
                          loading="lazy"
                          className="cursor-pointer object-contain rounded-md w-full h-auto max-h-96"
                        />
                      </div>
                    </PhotoView>
                  </PhotoProvider>
                ) : youtubeVideo ? (
                  <div className="w-full max-w-2xl mx-auto h-80">
                    <div className="relative w-full h-full rounded-md overflow-hidden bg-black">
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeVideo.videoId}?rel=0&modestbranding=1&enablejsapi=1`}
                        title={youtubeVideo.title}
                        className="w-full h-full border-0"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div className="absolute bottom-2 left-2 right-2 pointer-events-none">
                        <p className="text-white text-xs font-roboto-flex truncate bg-black bg-opacity-50 px-2 py-1 rounded">
                          {youtubeVideo.title}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  fileUrl && (
                    <div className="w-full max-w-sm mx-auto aspect-w-16 aspect-h-9">
                      <video
                        className="block mx-auto rounded-md focus:outline-none w-full h-full"
                        src={fileUrl}
                        controls
                      />
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Like/Comment Section */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-8">
                <Like post={post} />

                {allowComments && (
                  <button
                    className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors duration-200 font-roboto-flex group"
                    onClick={() => {
                      navigate(`/post/${post?._id}`, {
                        state: { from: location.pathname },
                      });
                    }}
                  >
                    <div className="p-1 rounded-full group-hover:bg-primary-50 transition-colors duration-200">
                      <HiOutlineChatBubbleOvalLeft className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-medium">Comment</span>
                  </button>
                )}
              </div>
            </div>

            {/* Add New Comment Form - Moved to top */}
            {allowComments && (
              <div className="mt-2">
                <div className="bg-gray-50 rounded-lg p-3 md:p-3 border border-gray-200 mb-2 mt-3 -ml-1">
                  <div className="flex items-start gap-2 md:gap-3">
                    <img
                      className="rounded-full w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 object-cover flex-shrink-0 mt-2 md:mt-3"
                      src={userData?.avatar || ''}
                      alt="user avatar"
                      loading="lazy"
                    />
                    <div className="flex-1">
                      <CommentForm
                        communityId={community?._id}
                        postId={post?._id}
                        className="compact"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
        :
        <div className="flex items-start gap-3 mb-1 md:mb-2">
          <div className="flex-shrink-0">
            <img
              className="rounded-full overflow-hidden w-12 h-12 object-cover"
              src={user.avatar}
              alt="user avatar"
              loading="lazy"
            />
          </div>
          <div className="flex-1">
            <div className="flex flex-col">
              {userData?._id === user?._id ? (
                <Link to="/profile" className="font-semibold text-sm md:text-base capitalize font-roboto-flex">
                  {user?.name}
                </Link>
              ) : (
                <Link to={`/profile/${user?.username || user?._id}`} className="font-semibold text-sm md:text-base capitalize font-roboto-flex">
                  {user?.name}
                </Link>
              )}
              <div className="flex items-center gap-2">
                {communityDisplay && (
                  communityDisplay.type === 'audience' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm">
                      {communityDisplay.name}
                    </span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/skills-feed/${communityDisplay.name}`}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 font-roboto-flex shadow-sm"
                      >
                        {communityDisplay.name}
                      </Link>
                      {communityDisplay.type === 'multiple' && communityDisplay.remainingCount > 0 && (
                        <div className="relative" ref={communityDropdownRef}>
                          <button
                            onClick={() => setShowCommunityDropdown(!showCommunityDropdown)}
                            className="text-xs text-primary-700 font-roboto-flex hover:text-primary-800 cursor-pointer"
                          >
                            +{communityDisplay.remainingCount} more
                          </button>

                          <Transition
                            show={showCommunityDropdown}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                              <div className="py-1">
                                {communityDisplay.allCommunities.slice(1).map((comm, index) => (
                                  <Link
                                    key={comm?._id}
                                    to={`/skills-feed/${comm.name}`}
                                    className="block px-3 py-2 text-xs font-roboto-flex"
                                    onClick={() => setShowCommunityDropdown(false)}
                                  >
                                    <span className="font-roboto-flex inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:from-primary-100 hover:to-primary-200 hover:border-primary-300 transition-all duration-200 shadow-sm">
                                      {comm.name}
                                    </span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </Transition>
                        </div>
                      )}
                    </div>
                  )
                )}
                <span className="text-gray-400 font-roboto-flex">|</span>
                <p className="text-xs text-gray-500 font-roboto-flex">{createdAt}</p>
              </div>
            </div>

            {/* Post Content */}
            <div className="mt-4 mb-4">
              <div className="font-roboto-flex break-words overflow-wrap-anywhere max-w-full text-xs md:text-sm">
                <RichTextContent content={content} />
              </div>

              {/* Media Section */}
              <div className="mt-1">
                {fileUrl && fileType.startsWith("image") ? (
                  <PhotoProvider
                    overlayRender={() => (
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-10 text-white px-3 py-2">
                        <p className="text-xs font-roboto-flex">{user.name}</p>
                        {communityDisplay && (
                          <p className="text-xs font-roboto-flex">{communityDisplay.name}</p>
                        )}
                        <p className="text-xs font-roboto-flex">{createdAt}</p>
                      </div>
                    )}
                  >
                    <PhotoView src={fileUrl}>
                      <div className="w-full max-w-3xl mx-auto">
                        <img
                          src={fileUrl}
                          alt={`${fileType} file`}
                          loading="lazy"
                          className="cursor-pointer object-contain rounded-md w-full h-auto max-h-96"
                        />
                      </div>
                    </PhotoView>
                  </PhotoProvider>
                ) : youtubeVideo ? (
                  <div className="w-full max-w-2xl mx-auto h-80">
                    <div className="relative w-full h-full rounded-md overflow-hidden bg-black">
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeVideo.videoId}?rel=0&modestbranding=1&enablejsapi=1`}
                        title={youtubeVideo.title}
                        className="w-full h-full border-0"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div className="absolute bottom-2 left-2 right-2 pointer-events-none">
                        <p className="text-white text-xs font-roboto-flex truncate bg-black bg-opacity-50 px-2 py-1 rounded">
                          {youtubeVideo.title}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  fileUrl && (
                    <div className="w-full max-w-sm mx-auto aspect-w-16 aspect-h-9">
                      <video
                        className="block mx-auto rounded-md focus:outline-none w-full h-full"
                        src={fileUrl}
                        controls
                      />
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Like/Comment Section */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-8">
                <Like post={post} />

                {allowComments && (
                  <button
                    className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors duration-200 font-roboto-flex group"
                    onClick={() => {
                      navigate(`/post/${post?._id}`, {
                        state: { from: location.pathname },
                      });
                    }}
                  >
                    <div className="p-1 rounded-full group-hover:bg-primary-50 transition-colors duration-200">
                      <HiOutlineChatBubbleOvalLeft className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-medium">Comment</span>
                  </button>
                )}
              </div>
            </div>

            {/* Add New Comment Form - Moved to top */}
            {allowComments && (
              <div className="mt-2">
                <div className="bg-gray-50 rounded-lg p-3 md:p-3 border border-gray-200 mb-2 mt-3 -ml-1">
                  <div className="flex items-start gap-2 md:gap-3">
                    <img
                      className="rounded-full w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 object-cover flex-shrink-0 mt-2 md:mt-3"
                      src={userData?.avatar || ''}
                      alt="user avatar"
                      loading="lazy"
                    />
                    <div className="flex-1">
                      <CommentForm
                        communityId={community?._id}
                        postId={post?._id}
                        className="compact"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      }

      {/* Delete Modal */}
      <DeleteModal
        showModal={showModal}
        postId={post?._id}
        onClose={() => toggleModal(false)}
        prevPath={location.state?.from || "/"}
      />

      {/* Edit Modal */}
      <EditPostModal
        showModal={showEditModal}
        post={post}
        onClose={() => toggleEditModal(false)}
      />

      {/* Publish Modal */}
      <PublishPostModal
        showModal={showPublishModal}
        post={post}
        onClose={() => togglePublishModal(false)}
      />


      {allowComments && (
        <div className="mt-2 w-full">
          {/* Comments Section */}
          <div className="bg-gray-50 rounded-lg p-1 border border-gray-200 w-full">
            <h3 className="text-sm md:text-base font-semibold text-gray-900 font-roboto-flex mb-3 md:mb-4">
              Comments ({comments?.length || 0})
            </h3>

            {/* Display comments with pagination */}
            <div className="mb-4 max-h-80 md:max-h-96 overflow-y-auto">
              <div className="space-y-3 md:space-y-4">
                {comments && comments.length > 0 ? (
                  comments.slice(0, visibleComments).map((comment, index) => (
                    <div key={comment._id || index} className="bg-white rounded-lg p-3 md:p-3 border border-gray-100 shadow-sm">
                      {/* Comment Header */}
                      <div className="flex items-start gap-2 md:gap-3 mb-3">
                        <img
                          className="rounded-full w-10 h-10 md:w-12 md:h-12 object-cover flex-shrink-0"
                          src={comment.user?.avatar || ''}
                          alt="commenter avatar"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm md:text-base font-semibold text-gray-900 font-roboto-flex capitalize">
                              {comment.user?.name}
                            </span>
                            <span className="text-xs text-gray-500 font-roboto-flex">
                              {comment.createdAt}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 font-roboto-flex break-words leading-relaxed">
                            {comment.content ? (
                              <div
                                className="font-roboto-flex prose max-w-none"
                                dangerouslySetInnerHTML={{ __html: renderRichWithMentions(comment.content) }}
                              />
                            ) : (
                              <i className="text-gray-400 font-roboto-flex">No content</i>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Reply Button and Show/Hide replies button */}
                      <div className="flex items-center gap-3 pl-12 md:pl-16">
                        <button
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium font-roboto-flex"
                          onClick={() => {
                            if (comment.user?.username) {
                              setReplyContent(`@${comment.user.username} `);
                            }
                            setActiveReply(activeReply === comment._id ? null : comment._id);
                          }}
                        >
                          {activeReply === comment._id ? "Cancel" : "Reply"}
                        </button>

                        {comment.replies && comment.replies.length > 0 && (
                          <button
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium font-roboto-flex"
                            onClick={() => toggleReplies(comment._id)}
                          >
                            {showReplies[comment._id] ? `Hide ${comment.replies.length} replies` : `Show ${comment.replies.length} replies`}
                          </button>
                        )}
                      </div>

                      {/* Reply Form */}
                      {activeReply === comment._id && (
                        <div className="mt-3 ml-8">
                          <div className="relative">
                            <textarea
                              ref={textareaRef}
                              className="w-full px-2 py-1 pb-6 border rounded-md focus:outline-none focus:border-primary-600 resize-none font-roboto-flex text-sm"
                              rows="1"
                              value={replyContent}
                              onChange={handleReplyChange}
                              maxLength={250}
                              placeholder="Write a reply..."
                              style={{ minHeight: '28px', maxHeight: '60px' }}
                            />
                            <div className="absolute bottom-1 right-2 text-xs text-gray-500 font-roboto-flex bg-white px-1 rounded">
                              {replyContent.length}/250
                            </div>
                          </div>
                          {showSuggestions && (
                            <MentionSuggestions
                              suggestions={suggestions}
                              onSelect={handleSelectUser}
                            />
                          )}
                          <div className="flex justify-end mt-1">
                            <button
                              className="bg-primary-700 text-white px-2 py-1 rounded text-xs font-roboto-flex"
                              disabled={replyLoading}
                              onClick={() => handleReplySubmit(comment._id)}
                            >
                              {replyLoading ? "Posting..." : "Reply"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Display replies if any */}
                      {comment.replies && comment.replies.length > 0 && showReplies[comment._id] && (
                        <div className="mt-3 ml-16 space-y-3">
                          {comment.replies.map((reply, replyIndex) => (
                            <div key={reply._id || replyIndex} className="flex items-start gap-2 md:gap-3">
                              <img
                                className="rounded-full w-10 h-10 md:w-12 md:h-12 object-cover flex-shrink-0"
                                src={reply.user?.avatar || ''}
                                alt="reply avatar"
                                loading="lazy"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm md:text-base font-semibold text-gray-800 font-roboto-flex capitalize">
                                    {reply.user?.name}
                                  </span>
                                  <span className="text-xs text-gray-500 font-roboto-flex">
                                    {reply.createdAt}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-700 font-roboto-flex break-words">
                                  {reply.content ? (
                                    <div
                                      className="font-roboto-flex prose max-w-none"
                                      dangerouslySetInnerHTML={{ __html: renderRichWithMentions(reply.content) }}
                                    />
                                  ) : (
                                    <i className="text-gray-400 font-roboto-flex">No content</i>
                                  )}
                                </p>

                                {/* Nested replies if any */}
                                {reply.replies && reply.replies.length > 0 && (
                                  <div className="mt-2 ml-16 space-y-2">
                                    {reply.replies.map((nestedReply, nestedIndex) => (
                                      <div key={nestedReply._id || nestedIndex} className="flex items-start gap-2 md:gap-3">
                                        <img
                                          className="rounded-full w-10 h-10 md:w-12 md:h-12 object-cover flex-shrink-0"
                                          src={nestedReply.user?.avatar || ''}
                                          alt="nested reply avatar"
                                          loading="lazy"
                                        />
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm md:text-base font-semibold text-gray-800 font-roboto-flex capitalize">
                                              {nestedReply.user?.name}
                                            </span>
                                            <span className="text-xs text-gray-500 font-roboto-flex">
                                              {nestedReply.createdAt}
                                            </span>
                                          </div>
                                          <p className="text-xs text-gray-700 font-roboto-flex break-words">
                                            {nestedReply.content ? (
                                              <div
                                                className="font-roboto-flex prose max-w-none"
                                                dangerouslySetInnerHTML={{ __html: renderRichWithMentions(nestedReply.content) }}
                                              />
                                            ) : (
                                              <i className="text-gray-400 font-roboto-flex">No content</i>
                                            )}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 font-roboto-flex">
                    No comments yet. Be the first to comment!
                  </div>
                )}
              </div>

              {/* Load More Button */}
              {comments && comments.length > visibleComments && (
                <div className="text-center mt-4">
                  <button
                    onClick={handleLoadMore}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium font-roboto-flex px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors duration-200"
                  >
                    Load more comments ({comments.length - visibleComments} remaining)
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// Add conditional render wrapper
const PostViewWrapper = ({ post, userData }) => {
  if (!post || typeof post !== 'object') {
    console.error('Post data is invalid:', post);
    return null;
  }
  return <PostView post={post} userData={userData} />;
};

export default PostViewWrapper;
